import PanelError from '@wandb/weave/common/components/elements/PanelError';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {Card, Placeholder} from 'semantic-ui-react';

interface VideoViewerProps {
  videoSrc: string;
  width: number;
  height: number;
  videoFilename?: string;
  headerElement?: ReactNode;
  footerElement?: ReactNode;
  loadingElement?: ReactNode;
  failedLoadElement?: ReactNode;
  refreshTimestamp?: number;
  mediaFailedToLoad?: boolean;
  single?: boolean;
  setDomLoadFailed?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  autoPlay?: boolean;
  muted?: boolean;
}

// a single image card
const VideoViewer = (props: VideoViewerProps) => {
  const {
    videoFilename,
    videoSrc,
    refreshTimestamp,
    headerElement,
    footerElement,
    loadingElement,
    failedLoadElement,
    mediaFailedToLoad,
    setDomLoadFailed,
    autoPlay,
    muted,
  } = props;

  const videoRef = useRef<HTMLVideoElement>(null);

  const [videoWidth, setVideoWidth] = useState<number>();
  const [videoHeight, setVideoHeight] = useState<number>();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [error, setError] = useState<ReactNode | null>(null);
  useLayoutEffect(() => {
    return () => setError(null);
  }, [videoSrc]);
  const onError = useCallback(() => {
    setDomLoadFailed?.(true);

    const err = videoRef.current?.error;
    if (err != null) {
      setError(getMediaErrorMessage(err));
    }
  }, [setDomLoadFailed]);

  const videoFile = videoFilename || videoSrc;
  const width = videoWidth || props.width;
  const height = videoHeight || props.height;

  // This section handles impartial data, streaming,
  // and errors in network requests

  // Create a refresh handler to refresh the dom node
  const videoRefresh = React.useCallback(() => {
    if (videoRef.current == null) {
      return;
    }
    const currentTime = videoRef.current.currentTime;
    videoRef.current.src = videoSrc;
    videoRef.current.currentTime = currentTime;
  }, [videoSrc]);

  useEffect(() => {
    videoRefresh();
  }, [videoRefresh, refreshTimestamp]);

  // Upon mount, unmute video if defaultUnmute is true
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted ?? true;
      if (autoPlay && videoRef.current.paused) {
        videoRef.current.play().catch(() => {
          // Browsers have their own implementations of how to handle auto-play and
          // many disallow it, for example: https://goo.gl/xX8pDD
        });
      }
    }
  }, [muted, autoPlay]);

  // Check the dom element and the loading spinner
  // In situations with no failures the dom will load first

  const onLoaded = (
    e: React.SyntheticEvent<HTMLVideoElement | HTMLImageElement, Event>
  ) => {
    const t = e.target;
    let w;
    let h;
    if ((t as HTMLVideoElement).videoWidth) {
      const v = t as HTMLVideoElement;
      w = v.videoWidth;
      h = v.videoHeight;
    } else {
      const i = t as HTMLImageElement;
      w = i.naturalWidth;
      h = i.naturalHeight;
    }
    setVideoWidth(w);
    setVideoHeight(h);
    setVideoLoaded(true);
  };

  let cardSize;
  if (!videoLoaded || mediaFailedToLoad) {
    cardSize = {maxWidth: 0, maxHeight: 0};
  }

  const cardStyles = {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    alignItems: 'stretch',
    boxShadow: 'none',
  };

  const mediaStyles = {
    ...{cursor: 'pointer', overflow: 'hidden'},
    ...cardSize,
    objectFit: 'contain' as const,
    width: '100%',
    height: '100%',
  };

  const loader = loadingElement ? (
    loadingElement
  ) : (
    <Placeholder style={{width, height}}>
      <Placeholder.Image />
    </Placeholder>
  );

  const content = (
    <>
      {!videoLoaded && loader}

      {!mediaFailedToLoad ? (
        <>
          {videoFile.match(/.gif$/) ? (
            <img
              src={videoSrc}
              alt={videoFile}
              style={mediaStyles}
              onLoad={onLoaded}
              onError={onError}
            />
          ) : (
            <div
              className="video-container"
              ref={containerRef}
              style={mediaStyles}>
              <video
                autoPlay={autoPlay ?? false}
                controls
                ref={videoRef}
                loop
                muted
                onLoadedData={onLoaded}
                onError={onError}
                src={videoSrc}
                playsInline // Prevents iOS from opening the video in full screen on autoplay
              />
            </div>
          )}
        </>
      ) : (
        failedLoadElement
      )}
    </>
  );
  return (
    <Card className="video-card content-card" style={cardStyles}>
      {headerElement}
      {error != null ? (
        <PanelError message={<div style={{width, height}}>{error}</div>} />
      ) : (
        content
      )}
      {footerElement}
    </Card>
  );
};

export default VideoViewer;

function getMediaErrorMessage({code, message}: MediaError): string {
  if (code === MediaError.MEDIA_ERR_ABORTED) {
    return `Fetching video was aborted: ${message}`;
  }
  if (code === MediaError.MEDIA_ERR_NETWORK) {
    return `Error fetching video over the network: ${message}`;
  }
  if (code === MediaError.MEDIA_ERR_DECODE) {
    return `Error decoding video: ${message}`;
  }
  if (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
    return `This video's codec is not supported by your browser. Please try another browser to view your logged video, or log it using a different codec.`;
  }
  return `Error displaying video: ${message}`;
}
